import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FETCH_DATA_TAG_REPORTS,
  FETCH_DATA_TAG_REPORT_CONTENT,
  CLEAR_DATA_TAG_SNACKBAR,
  UPLOAD_DATA_TAG_REPORT,
} from "./DataTagConstants";
import "../../components/views/GenerateReport/Report.scss";
import Page_Loader from "../../assets/vizr_images/page_loader.gif";
import { Button, Input, makeStyles, Tooltip } from "@material-ui/core";
import CloseIcon from "../../assets/vizr_images/ic_remove.svg";
import SearchIcon from "../../assets/vizr_images/ic_search.svg";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import GetAppIcon from "@material-ui/icons/GetApp";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { errorSnackbar } from "../../components/common/utilities";
import EnhancedTable from "../../components/shared/Table/Table";
import UploadReportModal from "./UploadReportModal";

const useStyles = makeStyles((theme) => ({
  reportSearchInput: {
    width: "350px",

    "& input": {
      padding: "5px 0px 5px 16px",

      "&::placeholder": {
        color: "#081981 !important",
        opacity: 1,
        fontSize: "16px",
      },
    },
  },

  reportActionButton: {
    cursor: "pointer",
    padding: "6px",
    margin: "0 5px",
    borderRadius: "50%",
    color: "#081981",
    backgroundColor: "#f1f4ff",
    transition: "all 0.2s ease",
    fontSize: "22px",

    "&:hover": {
      backgroundColor: "#081981",
      color: "#fff",
    },

    "&.disabled": {
      cursor: "not-allowed",
      opacity: 0.5,
      pointerEvents: "none",
    },
  },

  uploadReportButton: {
    backgroundColor: "#2d46ff",
    color: "#fff",
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 600,
    padding: "9px 20px",
    minWidth: "183px",
    borderRadius: "4px",
    marginBottom: "15px",

    "&:hover": {
      backgroundColor: "#2039e8",
    },
  },

  columnHeader: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    whiteSpace: "nowrap",
  },

  infoIcon: {
    fontSize: "15px",
    cursor: "pointer",
  },

  statusIcon: {
    color: "green",
    fontSize: "20px",
  },

  contentGenerated: {
    color: "#008c83",
    fontWeight: 600,
  },
}));

function DataTagReports({ retport_type = "dataTagReport" }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [openUploadSnackBar, setOpenUploadSnackBar] = useState(false);

  const tenantId = useSelector(
    (state) => state.DataTagReportsReducer?.allReports?.tenantId,
  );

  const [searchReportName, setSearchReportName] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [openSnackBar, setOpenSnackBar] = useState(false);

  const reportsResponse = useSelector(
    (state) => state.DataTagReportsReducer.allReports,
  );

  const dataTagReports = reportsResponse?.reports || [];
  const totalReportsCount = reportsResponse?.totalElements || 0;

  const loader = useSelector((state) => state.DataTagReportsReducer.loader);

  const snackBarMessage = useSelector(
    (state) => state.DataTagReportsReducer.snackBarMessage,
  );

  const contentAction = useSelector(
    (state) => state.DataTagReportsReducer.contentAction,
  );

  const uploadSnackBarMessage = useSelector(
    (state) => state.DataTagReportsReducer.uploadSnackBarMessage,
  );

  const uploadLoader = useSelector(
    (state) => state.DataTagReportsReducer.uploadLoader,
  );

  useEffect(() => {
    dispatch({
      type: FETCH_DATA_TAG_REPORTS,
      payload: {
        reportType: retport_type,
        page: currentPage,
        size: recordsPerPage,
        search: appliedSearch,
      },
    });

    return () => {
      setOpenSnackBar(false);
      dispatch({ type: CLEAR_DATA_TAG_SNACKBAR });
    };
  }, [retport_type, currentPage, recordsPerPage, appliedSearch, dispatch]);

  useEffect(() => {
    if (snackBarMessage && snackBarMessage.status && snackBarMessage.message) {
      setOpenSnackBar(true);
    }
  }, [snackBarMessage]);

  useEffect(() => {
    if (
      uploadSnackBarMessage &&
      uploadSnackBarMessage.status &&
      uploadSnackBarMessage.message
    ) {
      setOpenUploadSnackBar(true);

      if (uploadSnackBarMessage.status === "success") {
        setUploadModalOpen(false);
      }
    }
  }, [uploadSnackBarMessage]);

  const handlePopUpClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenSnackBar(false);
    dispatch({ type: CLEAR_DATA_TAG_SNACKBAR });
  };

  const handleChangeRowsPerPage = (value) => {
    setCurrentPage(0);
    setRecordsPerPage(value);
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const renderNull = () => {
    return <>-</>;
  };

  const renderPeriod = (params) => {
    if (!params || !params.reportPeriod) {
      return renderNull();
    }

    return (
      <div className="report_titles" title={params.reportPeriod}>
        {params.reportPeriod}
      </div>
    );
  };

  const renderGeneratedOnHeader = () => {
    return (
      <div className={classes.columnHeader}>
        <span>Generated On</span>
        <Tooltip title="UTC Time Format" placement="top">
          <InfoOutlinedIcon className={classes.infoIcon} />
        </Tooltip>
      </div>
    );
  };

  const renderReportPeriodHeader = () => {
    return (
      <div className={classes.columnHeader}>
        <span>Report Period</span>
        {/* <Tooltip title="UTC Time Format" placement="top">
          <InfoOutlinedIcon className={classes.infoIcon} />
        </Tooltip> */}
      </div>
    );
  };

  const renderGeneratedAt = (params) => {
    if (!params || !params.generatedOn) {
      return renderNull();
    }

    const date = new Date(params.generatedOn);

    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const day = String(date.getUTCDate()).padStart(2, "0");
    const year = date.getUTCFullYear();

    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");

    const formattedDate = `${month}-${day}-${year}`;
    const formattedTime = `${hours}:${minutes}`;

    return (
      <div>
        <div className="report_titles">{formattedDate}</div>
        <div className="report_titles">{formattedTime}</div>
      </div>
    );
  };

  const renderReportName = (params) => {
    if (!params || !params.reportName) {
      return renderNull();
    }

    return (
      <div className="report_titles name" title={params.reportName}>
        {params.reportName}
      </div>
    );
  };

  const renderDescription = (params) => {
    if (!params || !params.description) {
      return renderNull();
    }

    return (
      <div className="report_titles description" title={params.description}>
        {params.description}
      </div>
    );
  };

  const renderGeneratedBy = (params) => {
    if (!params || !params.generatedBy) {
      return renderNull();
    }

    return (
      <div className="report_titles generateBy" title={params.generatedBy}>
        {params.generatedBy}
      </div>
    );
  };

  const renderContentGenerated = (params) => {
    if (!params || !params.contentGenerated) {
      return renderNull();
    }

    return (
      <div
        className="report_titles content contentGenerated"
        title={params.contentGenerated}
      >
        {params.contentGenerated}
      </div>
    );
  };

  const renderStatus = (params) => {
    if (!params || !params.status) {
      return renderNull();
    }

    if (params.status === "Success") {
      return <CheckCircleIcon className={classes.statusIcon} />;
    }

    return <div className="report_titles">{params.status}</div>;
  };

  const getPreviewTitle = (report) => {
    return `Druvstar - Data Security & Governance Report (${report?.reportName || "Report"})`;
  };

  const handlePreviewReport = (report) => {
    dispatch({
      type: FETCH_DATA_TAG_REPORT_CONTENT,
      payload: {
        reportId: report.reportId,
        mode: "preview",
        report,
        previewTitle: getPreviewTitle(report),
        tenantId: tenantId,
      },
    });
  };

  const handleDownloadReport = (report) => {
    dispatch({
      type: FETCH_DATA_TAG_REPORT_CONTENT,
      payload: {
        reportId: report.reportId,
        mode: "download",
        report,
      },
    });
  };

  const handleUploadReport = () => {
    setUploadModalOpen(true);
  };

  const renderActions = (params) => {
    if (!params || !params.reportId) {
      return renderNull();
    }

    const isRowBusy = contentAction?.reportId === params.reportId;

    return (
      <div className="side-icons report-actions">
        {isRowBusy ? (
          <img
            src={Page_Loader}
            alt="loading"
            className="report-action-loader"
          />
        ) : (
          <OpenInNewIcon
            className={classes.reportActionButton}
            titleAccess="Preview Report"
            onClick={() => handlePreviewReport(params)}
          />
        )}

        {/* {!params.isInsightsReport && (
        <GetAppIcon
          className={classes.reportActionButton}
          titleAccess="Download Report"
          onClick={() => !isRowBusy && handleDownloadReport(params)}
        />
      )} */}
      </div>
    );
  };

  const handleChange = (e) => {
    setSearchReportName(e.target.value);
  };

  const handleSearch = () => {
    setCurrentPage(0);
    setAppliedSearch(searchReportName);
  };

  const handleClearSearch = () => {
    setSearchReportName("");
    setAppliedSearch("");
    setCurrentPage(0);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleUploadSnackBarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenUploadSnackBar(false);
  };

  const renderSearchBar = () => {
    return (
      <div className="repositorySearch">
        <div className="reportSearchBar">
          <Input
            value={searchReportName}
            type="text"
            placeholder="Search here..."
            onKeyPress={handleKeyPress}
            disableUnderline
            className={classes.reportSearchInput}
            onChange={handleChange}
          />

          <Button
            className="searchCancelIcon"
            onClick={handleClearSearch}
            aria-label="close"
          >
            <img src={CloseIcon} alt="close" />
          </Button>

          <hr />

          <Button
            className="searchIcon"
            onClick={handleSearch}
            aria-label="search"
          >
            <img src={SearchIcon} alt="search" />
          </Button>
        </div>
      </div>
    );
  };

  const reportColumns = [
    {
      title: "Report Name",
      render: renderReportName,
      width: "11%",
    },
    {
      title: "Description",
      render: renderDescription,
      width: "25%",
    },
    {
      title: renderGeneratedOnHeader(),
      render: renderGeneratedAt,
      orderId: "generatedOn",
      width: "10%",
    },
    {
      title: renderReportPeriodHeader(),
      render: renderPeriod,
      width: "11%",
    },
    {
      title: "Generated By",
      render: renderGeneratedBy,
      width: "13%",
    },
    {
      title: "Content Generated",
      render: renderContentGenerated,
      width: "16%",
    },
    {
      title: "Status",
      render: renderStatus,
      width: "6%",
    },
    {
      title: "Actions",
      render: renderActions,
      width: "8%",
    },
  ];

  const renderTable = () => {
    return (
      <EnhancedTable
        cols={reportColumns}
        data={dataTagReports}
        totalCount={totalReportsCount}
        loc="dataTagReport"
        compact
        currentPage={currentPage}
        recordsPerPage={recordsPerPage}
        onChangeRowsPerPage={handleChangeRowsPerPage}
        onChangePage={handleChangePage}
      />
    );
  };

  const renderDataTagReportsTable = () => {
    return (
      <>
        <div className="previously-generated-reports-title">
          Previously Generated Reports
        </div>

        <div className="reports-table-wrapper">
          <div className="reports-upload-container">
            <Button
              variant="contained"
              className={classes.uploadReportButton}
              startIcon={<CloudUploadIcon />}
              onClick={handleUploadReport}
            >
              Upload Report
            </Button>
          </div>

          <div className="custom-data-grid-main dataEstate-report">
            {renderTable()}
          </div>
        </div>
      </>
    );
  };

  const renderMessage = () => {
    return (
      <>
        {snackBarMessage &&
          snackBarMessage.status &&
          snackBarMessage.message &&
          errorSnackbar(
            openSnackBar,
            handlePopUpClose,
            snackBarMessage.message,
            snackBarMessage.status,
          )}
      </>
    );
  };

  return (
    <>
      {loader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source reports datatag-reports-loader"
        />
      )}

      {renderDataTagReportsTable()}

      {renderMessage()}

      {uploadSnackBarMessage &&
        uploadSnackBarMessage.status &&
        uploadSnackBarMessage.message &&
        errorSnackbar(
          openUploadSnackBar,
          handleUploadSnackBarClose,
          uploadSnackBarMessage.message,
          uploadSnackBarMessage.status,
        )}

      <UploadReportModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        reportType={retport_type}
      />
    </>
  );
}

export default DataTagReports;
